import type { TypedSupabaseClient } from '../client';
import type {
  IAgentRepository,
  AgentRun,
  AgentRunFilters,
  AgentRunOutcome,
  AgentStep,
  AgentStepKind,
  AgentTurnInput,
  AgentTurnLanguage,
  AgentTurnResult,
  RecordAgentStepInput,
  StartAgentRunInput,
} from '../../../interfaces/src';
import { functionError } from './function-error';
import { newAiOperationId } from './ai-operation-id';

// `agent_runs` / `agent_steps` ne sont pas encore dans les types générés :
// même échappatoire que `push_tokens` (push-tokens-repository.ts) en
// attendant un `supabase gen types`. Le `any` est cantonné à cette ligne.
const anyFrom = (client: TypedSupabaseClient) => (table: string) =>
  (client as unknown as { from: (t: string) => any }).from(table);

/** Postgres NUMERIC comes back from PostgREST as a string — coerce safely. */
function num(v: unknown): number {
  if (typeof v === 'number') return v;
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Transport de l'agent conversationnel.
 *
 * `turn()` est un proxy vers la fonction edge `gesty-agent`, qui détient la
 * clé du fournisseur de modèle et débite le portefeuille IA. Le reste est du
 * journal : `agent_runs` porte l'exécution, `agent_steps` ses étapes. Aucune
 * décision métier ici — le catalogue d'actions, les garde-fous et les aperçus
 * vivent dans `libs/domain/agent`, et la boucle est orchestrée côté app.
 *
 * `tenant_id` retombe sur le défaut SQL (`current_user_tenant_id()`) comme
 * pour les fils de support : la RLS reste la source du cloisonnement.
 */
export class SupabaseAgentRepository implements IAgentRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async turn(input: AgentTurnInput): Promise<AgentTurnResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'gesty-agent',
      {
        body: {
          operationId: newAiOperationId(),
          runId: input.runId,
          messages: input.messages,
          tools: input.tools,
          language: input.language,
          languageInstruction: input.languageInstruction,
          screenContext: input.screenContext,
        },
      },
    );
    if (error) throw await functionError(error, 'agent_turn_failed');
    const res = data as {
      content?: string;
      toolCalls?: RawToolCall[];
      costCredits?: unknown;
      balanceCredits?: unknown;
      model?: string;
    } | null;
    return {
      content: res?.content?.trim() ?? '',
      toolCalls: (res?.toolCalls ?? []).map(mapToolCall),
      costCredits: num(res?.costCredits),
      balanceCredits: num(res?.balanceCredits),
      model: res?.model ?? '',
    };
  }

  async startRun(input: StartAgentRunInput): Promise<AgentRun> {
    const { data, error } = await anyFrom(this.supabase)('agent_runs')
      .insert({ language: input.language, title: input.title })
      .select(RUN_COLUMNS)
      .single();
    if (error) throw error;
    return mapAgentRun(data as AgentRunRow);
  }

  async recordStep(input: RecordAgentStepInput): Promise<AgentStep> {
    const { data, error } = await anyFrom(this.supabase)('agent_steps')
      .insert({ run_id: input.runId, kind: input.kind, payload: input.payload })
      .select(STEP_COLUMNS)
      .single();
    if (error) throw error;
    return mapAgentStep(data as AgentStepRow);
  }

  async finishRun(runId: string, outcome: AgentRunOutcome): Promise<void> {
    const { error } = await anyFrom(this.supabase)('agent_runs')
      // `updated_at` is owned by the database trigger. Keeping it out of the
      // client payload also lets the journal RLS revoke that column entirely.
      .update({ outcome })
      .eq('id', runId);
    if (error) throw error;
  }

  async getRun(runId: string): Promise<AgentRun | null> {
    const { data, error } = await anyFrom(this.supabase)('agent_runs')
      .select(RUN_COLUMNS)
      .eq('id', runId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapAgentRun(data as AgentRunRow) : null;
  }

  async listRuns(filters?: AgentRunFilters): Promise<AgentRun[]> {
    let q = anyFrom(this.supabase)('agent_runs')
      .select(RUN_COLUMNS)
      .order('created_at', { ascending: false });
    // Seul `applied` marque une écriture réellement passée en base : les
    // autres issues (répondu, annulé, refusé) n'ont rien modifié.
    if (filters?.onlyWithWrites) q = q.eq('outcome', 'applied');
    q = q.limit(filters?.limit ?? 50);
    const { data, error } = await q;
    if (error) throw error;
    return ((data ?? []) as AgentRunRow[]).map(mapAgentRun);
  }

  async listSteps(runId: string): Promise<AgentStep[]> {
    const { data, error } = await anyFrom(this.supabase)('agent_steps')
      .select(STEP_COLUMNS)
      .eq('run_id', runId)
      .order('step_index', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as AgentStepRow[]).map(mapAgentStep);
  }
}

const RUN_COLUMNS =
  'id, language, title, step_count, spent_credits, outcome, created_at, updated_at';
const STEP_COLUMNS =
  'id, run_id, step_index, kind, payload, cost_credits, created_at';

interface RawToolCall {
  id?: string;
  name?: string;
  arguments?: unknown;
}

/**
 * Le modèle rend parfois les arguments déjà désérialisés selon le fournisseur.
 * On les re-sérialise pour que `parseIntent` reçoive toujours la même forme.
 */
function mapToolCall(call: RawToolCall): AgentTurnResult['toolCalls'][number] {
  const raw = call.arguments;
  return {
    id: call.id ?? '',
    name: call.name ?? '',
    arguments: typeof raw === 'string' ? raw : JSON.stringify(raw ?? {}),
  };
}

interface AgentRunRow {
  id: string;
  language: string;
  title: string | null;
  step_count: unknown;
  spent_credits: unknown;
  outcome: string | null;
  created_at: string;
  updated_at: string | null;
}

function mapAgentRun(r: AgentRunRow): AgentRun {
  return {
    id: r.id,
    language: r.language as AgentTurnLanguage,
    title: r.title ?? '',
    stepCount: num(r.step_count),
    spentCredits: num(r.spent_credits),
    outcome: (r.outcome as AgentRunOutcome | null) ?? null,
    createdAt: r.created_at,
    // Une exécution encore ouverte n'a pas été retouchée depuis sa création.
    updatedAt: r.updated_at ?? r.created_at,
  };
}

interface AgentStepRow {
  id: string;
  run_id: string;
  step_index: unknown;
  kind: string;
  payload: Record<string, unknown> | null;
  cost_credits: unknown;
  created_at: string;
}

function mapAgentStep(r: AgentStepRow): AgentStep {
  return {
    id: r.id,
    runId: r.run_id,
    index: num(r.step_index),
    kind: r.kind as AgentStepKind,
    payload: r.payload ?? {},
    costCredits: num(r.cost_credits),
    createdAt: r.created_at,
  };
}
